/**
 * TableToolbar — barra de filtros unificada para TODAS las tablas del sistema.
 *
 * Diseño único: búsqueda a la izquierda, acciones a la derecha (select / pills /
 * ordenar / botón Filtros), y un panel desplegable con los campos de filtro.
 *
 * Uso típico:
 *   <TableToolbar
 *     search={search} onSearch={setSearch} searchPlaceholder="Buscar…"
 *     loading={loading}
 *     extra={<FilterPills .../>}          // controles inline opcionales
 *     sort={<SortButton .../>}            // botón de orden opcional
 *     activeFilterCount={n}
 *     onClearFilters={limpiar}
 *     filters={<><FilterField .../></>}   // campos del panel; si hay → muestra botón Filtros
 *   />
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./TableToolbar.module.css";

export function TableToolbar({
  search, onSearch, searchPlaceholder = "Buscar…", loading = false,
  extra = null, sort = null, rightActions = null,
  filters = null, activeFilterCount = 0, onClearFilters,
  defaultOpen = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={styles.toolbar}>
      <div className={styles.bar}>
        {/* Búsqueda */}
        {onSearch && (
          <div className={styles.search}>
            <i className={`fa-solid fa-magnifying-glass ${styles.searchIcon}`}
               style={loading ? { color: "#f97316" } : undefined} />
            <input
              type="text"
              // Es un buscador, no un campo de usuario: sin esto Chrome le
              // rellenaba el usuario guardado cuando la página tenía un campo
              // de contraseña (p. ej. el modal de Editar Residente).
              autoComplete="off" name="busqueda-tabla"
              value={search ?? ""}
              placeholder={searchPlaceholder}
              onChange={(e) => onSearch(e.target.value)}
            />
            {search && (
              <button type="button" className={styles.clearSearch} onClick={() => onSearch("")}>
                ✕
              </button>
            )}
          </div>
        )}

        {/* Acciones a la derecha */}
        <div className={styles.actions}>
          {extra}
          {sort}
          {filters && (
            <button type="button"
              className={`${styles.btn} ${activeFilterCount ? styles.btnActive : ""}`}
              onClick={() => setOpen((o) => !o)}>
              <i className="fa-solid fa-filter" />
              {t("common.filtros")}
              {activeFilterCount > 0 && <span className={styles.badge}>{activeFilterCount}</span>}
            </button>
          )}
          {rightActions}
        </div>
      </div>

      {/* Panel desplegable */}
      {filters && open && (
        <div className={styles.panel}>
          <div className={styles.grid}>{filters}</div>
          {activeFilterCount > 0 && onClearFilters && (
            <div className={styles.footer}>
              <span className={styles.activeNote}>
                <i className="fa-solid fa-circle-dot" /> {activeFilterCount} {t("common.filtrosActivos")}
              </span>
              <button type="button" className={styles.clearBtn} onClick={onClearFilters}>
                <i className="fa-solid fa-xmark" /> {t("common.limpiarTodo")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Campo de filtro (label + contenido) ───────────────────────────────────────
export function FilterField({ label, children }) {
  return (
    <div className={styles.field}>
      {label && <label className={styles.fieldLabel}>{label}</label>}
      {children}
    </div>
  );
}

export function FilterInput({ type = "text", ...props }) {
  return <input type={type} className={styles.input} {...props} />;
}

export function FilterSelect({ children, ...props }) {
  return <select className={styles.input} {...props}>{children}</select>;
}

// ── Grupo de checkboxes ───────────────────────────────────────────────────────
export function FilterCheckGroup({ options, selected = [], onChange }) {
  return (
    <div className={styles.checkGroup}>
      {options.map((opt) => {
        const isSel = selected.includes(opt.value);
        return (
          <label key={opt.value}
            className={`${styles.checkItem} ${isSel ? styles.checkItemActive : ""}`}>
            <input type="checkbox" className={styles.checkInput} checked={isSel}
              onChange={() => onChange(isSel ? selected.filter((v) => v !== opt.value) : [...selected, opt.value])} />
            <span className={styles.checkMark}>{isSel && <i className="fa-solid fa-check" />}</span>
            <span className={styles.checkLabel}>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

// ── Pills segmentados (estado / vistas inline) ────────────────────────────────
// options: [{ value, label, icon? }]
export function FilterPills({ options, value, onChange }) {
  return (
    <div className={styles.pills}>
      {options.map((opt) => (
        <button key={opt.value} type="button"
          className={`${styles.pill} ${value === opt.value ? styles.pillActive : ""}`}
          onClick={() => onChange(opt.value)}>
          {opt.icon && <i className={`fa-solid ${opt.icon}`} />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Select inline (tipo, etc.) ────────────────────────────────────────────────
export function ToolbarSelect({ children, active = false, ...props }) {
  return (
    <select className={styles.select}
      style={active ? { color: "#f97316", borderColor: "#f97316" } : undefined} {...props}>
      {children}
    </select>
  );
}

// ── Botón de orden (toggle) ───────────────────────────────────────────────────
// state: "none" | "asc" | "desc"; onToggle cicla
export function SortButton({ state = "none", label, onToggle }) {
  const icon = state === "desc" ? "fa-sort-down" : state === "asc" ? "fa-sort-up" : "fa-sort";
  return (
    <button type="button"
      className={`${styles.btn} ${state !== "none" ? styles.btnActive : ""}`}
      onClick={onToggle}>
      <i className={`fa-solid ${icon}`} />
      {label}
    </button>
  );
}
